import { FormattedMessage, useMelioIntl } from '@melio/ar-domain';
import { Group, SelectionCard, Text } from '@melio/penny';
import { FundingSource } from '@melio/platform-api';
import { forwardRef } from '@melio/platform-utils';
import React, { useState } from 'react';

import { AddBankFundingSourceFormFields, CardHolderDetailsFormFields, InvoicePaymentRequestDetails } from '../types';
import { AddBankFundingSourceForm } from './AddBankFundingSourceForm';
import { AddCardFundingSourceForm } from './AddCardFundingSourceForm';
import { PCIComplienceInfo } from './PCIComplienceInfo';

export type FundingSourceSelectionProps = {
  onSubmitByCard: (formValues: CardHolderDetailsFormFields) => void;
  onSubmitByBankAccount: (formValues: AddBankFundingSourceFormFields) => void;
  isSaving: boolean;
  amount: number;
  fundingSourceName?: string;
  onDeleteBankAccount: VoidFunction;
  paymentOptions: InvoicePaymentRequestDetails['paymentOptions'];
};

export const FundingSourceSelection = forwardRef<FundingSourceSelectionProps>(
  (
    { onDeleteBankAccount, fundingSourceName, onSubmitByCard, onSubmitByBankAccount, isSaving, amount, paymentOptions },
    ref
  ) => {
    const { formatMessage } = useMelioIntl();
    const initialFundingSource = paymentOptions.isCardAllowed
      ? 'card'
      : paymentOptions.isAchAllowed
      ? 'bank-account'
      : null;
    const shouldRenderTabsComponent = paymentOptions.isCardAllowed && paymentOptions.isAchAllowed;
    const [selectedFundingSource, setSelectedFundingSource] = useState<FundingSource['type'] | null>(
      initialFundingSource
    );

    return (
      <Group variant="vertical" ref={ref}>
        <Group variant="vertical" spacing="m">
          {shouldRenderTabsComponent ? (
            <>
              <Text textStyle="body2Semi" color="neutral.black">
                <FormattedMessage id="ar.newGuestPayment.paymentMethods.tabs.title.text" />
              </Text>
              <Group>
                <SelectionCard
                  icon="credit-card"
                  mainLabelProps={{
                    label: formatMessage('ar.newGuestPayment.paymentMethods.tabs.card.label'),
                  }}
                  onClick={() => setSelectedFundingSource('card')}
                  isSelected={selectedFundingSource === 'card'}
                />
                <SelectionCard
                  icon="bank"
                  data-testid="add-bank-funding-source-tab"
                  mainLabelProps={{
                    label: formatMessage('ar.newGuestPayment.paymentMethods.tabs.bank.label'),
                  }}
                  onClick={() => setSelectedFundingSource('bank-account')}
                  isSelected={selectedFundingSource === 'bank-account'}
                />
              </Group>
            </>
          ) : null}
          <Group variant="vertical">
            {selectedFundingSource === 'card' ? (
              <AddCardFundingSourceForm
                onSubmit={(formValues) => onSubmitByCard(formValues)}
                isSaving={isSaving}
                amount={amount}
              />
            ) : (
              <AddBankFundingSourceForm
                onSubmit={(formValues) => onSubmitByBankAccount(formValues)}
                isSaving={isSaving}
                fundingSourceName={fundingSourceName}
                amount={amount}
                onDeleteBankAccount={onDeleteBankAccount}
              />
            )}
          </Group>
        </Group>
        <PCIComplienceInfo />
      </Group>
    );
  }
);

FundingSourceSelection.displayName = 'SelectFundingSource';
